import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { MoneyCell, StatusCell, DateCell, PaymentErrors } from 'common/components';

import { withTheme } from 'common/styling/theme';
import { CrossIcon, DepositIcon, DoneIcon, WaitIcon } from 'common/icons';
import { useMediaQuery } from 'react-responsive';
import InstructionIcon from '../InstractionIcon';
import { staticStyles, getDynamicStyles } from './style';
import PaymentStatus from '../PaymentStatus';
import PaymentSuccess from '../PaymentSuccess';

const STATUS_CHANGED = 'status_changed';

const PaymentsInfoPanel = ({ data, addons, paymentSystemInstruction, isDeposit, theme, isRequiresUserAction }) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const paymentsInfoData = useMemo(
    () => [
      { titleId: 'justID', item: data.id },
      {
        titleId: 'justDate',
        item: <DateCell hideTime value={data.created} />,
      },
      { titleId: 'justPaymentMethod', item: data.paymentMethodTitle },
      {
        titleId: 'justCommission',
        item: <MoneyCell tooltipId="payments-info-data-commision" value={data.commission} heightMaxContent />,
      },
      ...(!addons.includes('wallet') ? [{ titleId: 'justTradingAccount', item: data.accountLogin }] : []),
    ],
    [data]
  );

  const doneStatusGroup = {
    title: isDeposit ? 'justReceived' : 'justSent',
    icon: <DoneIcon />,
    color: 'success',
    options: ['deposited', 'received', 'done', 'executed'],
  };

  const statusGroups = [
    {
      title: 'justCreated',
      icon: '',
      color: 'created',
      options: ['pmr_init', 'initialized'],
    },
    {
      title: 'justVerification',
      icon: <WaitIcon />,
      color: 'verification',
      options: ['waiting_verification', 'waiting_ps_verification', 'id_verification', 'payment_proof'],
    },
    {
      title: 'justProcessing',
      icon: <WaitIcon />,
      color: 'processing',
      options: [
        'money_waiting',
        'in_process',
        'depositing',
        'waiting_approval',
        'waiting_confirmation',
        'processing',
        'withdrawing',
        'payout',
        'need_finish',
        'created',
        'approved',
        'waiting_for_processing',
        'proof_uploaded',
        'proof_verified',
        'proof_rejected',
      ],
    },
    {
      title: 'justFailed',
      icon: <CrossIcon />,
      color: 'failed',
      options: ['failed', 'rejected', 'canceled'],
    },
    doneStatusGroup,
  ];

  const matchStatusGroup = (statusGroup, status) =>
    statusGroup.options.some(option => option === status || `marked_${option}` === status);

  const getEventStatusGroup = event => statusGroups.find(statusGroup => matchStatusGroup(statusGroup, event.status));

  const events = [...(data.events.length > 0 ? data.events : [{ status: data.status, updated: data.created }])]
    .sort((a, b) => new Date(a.updated) - new Date(b.updated))
    .reduceRight((acc, event, index, array) => {
      if (event.status === STATUS_CHANGED) {
        if (index !== array.length - 1) {
          return acc;
        }
        event.status = data.status;
      }
      const statusGroup = getEventStatusGroup(event);
      // for tests
      if (!array.findLastIndex) {
        acc.unshift(event);
        return acc;
      }
      if (index === array.findLastIndex(event => getEventStatusGroup(event) === statusGroup)) {
        acc.unshift(event);
        return acc;
      }
      if (!event.newEvent) {
        acc[0].newEvent = false;
      }
      return acc;
    }, []);
  const lastEvent = events[events.length - 1];
  const isDone = matchStatusGroup(doneStatusGroup, lastEvent?.status);

  return (
    <div className="PaymentsInfoPanelWrap">
      <div className="PaymentsInfoPanel">
        <div className="PaymentsInfoPanel__header">
          <div className="PaymentsInfoPanel__header-left">
            <div className="PaymentsInfoPanel__header-left-icon">
              <DepositIcon />
            </div>

            <div className="PaymentsInfoPanel__header-left-title">
              <FormattedMessage id="justAmount">
                {txt => <p className="PaymentsInfoPanel__header-left-title-amount">{txt}:</p>}
              </FormattedMessage>
              <div className="PaymentsInfoPanel__header-left-title-value">
                <MoneyCell tooltipId="payments-info-data-amount" value={data.amountWithCommission} heightMaxContent />
              </div>
            </div>
          </div>
          <div className="PaymentsInfoPanel__header-status">
            <StatusCell statusCode={lastEvent?.status} withBackground={isMobile} />
          </div>
        </div>
        <div className="PaymentsInfoPanel__main">
          <div className="PaymentsInfoPanel__main-left-content">
            <div className="PaymentsInfoPanel__main-statuses">
              {events.map(event => (
                <PaymentStatus
                  key={event.updated}
                  event={event}
                  isLastEvent={event === lastEvent}
                  statusGroup={getEventStatusGroup(event)}
                  isRequiresUserAction={isRequiresUserAction}
                />
              ))}
            </div>
            {isRequiresUserAction && (
              <div className="PaymentsInfoPanel__main-requires-action">
                <FormattedMessage id="actionRequiredTitle">{txt => <h4>{txt}</h4>}</FormattedMessage>
                <FormattedMessage id="actionRequiredDescription">
                  {txt => <p className="PaymentsInfoPanel__main-requires-action-text">{txt}</p>}
                </FormattedMessage>
              </div>
            )}
          </div>
          {isDone && <PaymentSuccess isDeposit={isDeposit} />}
        </div>

        <div className="PaymentInfo">
          {paymentsInfoData.map(({ titleId, item }) => (
            <div
              key={`payments-info-panel-item-${titleId}`}
              id={`client-dw-${isDeposit ? 'deposit' : 'withdrawal'}-${titleId}`}
              className="PaymentInfo__item">
              <span className="PaymentInfo__item-title">
                <FormattedMessage id={titleId}>{txt => txt}</FormattedMessage>
              </span>
              <span className="PaymentInfo__item-value">{item}</span>
            </div>
          ))}
        </div>

        <PaymentErrors displayErrors={data.displayErrors} />
      </div>

      {paymentSystemInstruction && (
        <div className="Instruction">
          <div className="Instruction__Icon">
            <InstructionIcon />
          </div>
          <div className="Instruction__CustomMessage" dangerouslySetInnerHTML={{ __html: paymentSystemInstruction }} />
        </div>
      )}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

PaymentsInfoPanel.propTypes = {
  data: PropTypes.object,
  addons: PropTypes.array,
  paymentSystemInstruction: PropTypes.string,
  isDeposit: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  isRequiresUserAction: PropTypes.bool.isRequired,
};

PaymentsInfoPanel.defaultProps = {
  data: {},
  addons: [],
  theme: {},
  paymentSystemInstruction: '',
};

export default withTheme()(PaymentsInfoPanel);
export { PaymentsInfoPanel };
